import { Role, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { Status, UserResponse } from 'Models/User';

/**
 * Determines how User names are formatted.
 */
export enum UserNameFormat {
    FIRST_SPACE_LAST,
    LAST_COMMA_FIRST,
}

/**
 * Return the User's department when the User is provided as a subject within a list of Users.
 */
export const getDepartmentFromSubject = (subject: string, users: UserResponse[]): string | undefined => {
    const user = users.find((user) => user.cognito_subject === subject);
    if (user) {
        return user.department;
    } else {
        // TODO: Is this necessary? Will a user ever not be found? If this is removed, remove non‑null assertion operators (i.e., !) from calls to this function.
        return undefined;
    }
};

/**
 * Return the User's name in a standard format when the User is provided as an object.
 * @param format - Use UserNameFormat.FIRST_SPACE_LAST when showing a single user. Otherwise, use the default (UserNameFormat.LAST_COMMA_FIRST) when multiple users are displayed to make sorting possible.
 */
export const getUserNameFromUser = (user: UserResponse, format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): string => {
    return applyUserNameFormatting(user, format);
};

/**
 * Return the User's name in a standard format when the User is provided as a subject within a list of Users.
 * @param format - Use UserNameFormat.FIRST_SPACE_LAST when showing a single user. Otherwise, use the default (UserNameFormat.LAST_COMMA_FIRST) when multiple users are displayed to make sorting possible.
 */
export const getUserNameFromSubject = (subject: string, users: UserResponse[], format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): string | undefined => {
    const user = users.find((user) => user.cognito_subject === subject);
    if (user) {
        return applyUserNameFormatting(user, format);
    } else {
        // TODO: Is this necessary? Will a user ever not be found? If this is removed, remove non‑null assertion operators (i.e., !) from calls to this function.
        return undefined;
    }
};

/**
 * Standardize how Users are displayed throughout the application.
 * @param format - Use UserNameFormat.FIRST_SPACE_LAST when showing a single user. Otherwise, use the default (UserNameFormat.LAST_COMMA_FIRST) when multiple users are displayed to make sorting possible.
 */
const applyUserNameFormatting = (user: UserResponse, format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): string => {
    let name = '';
    if (format === UserNameFormat.LAST_COMMA_FIRST) {
        name = `${user.last_name}, ${user.first_name}`;
    } else {
        name = `${user.first_name} ${user.last_name}`;
    }

    if (user.status === Status.INACTIVE) {
        return `${name} (Inactive)`;
    } else {
        return name;
    }
};

/**
 * Given two User subjects, compare the names of their corresponding User objects for sorting.
 */
export const compareUsersBySubjectForSorting = (subject1: string, subject2: string, users: UserResponse[], format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): number => {
    const user_name_a = getUserNameFromSubject(subject1, users, format);
    const user_name_b = getUserNameFromSubject(subject2, users, format);

    if (user_name_a && user_name_b) {
        return user_name_a.localeCompare(user_name_b);
    }

    if (user_name_a && !user_name_b) {
        return -1;
    }

    if (!user_name_a && user_name_b) {
        return 1;
    }

    return 0;
};

/**
 * Standardizes sorting for UserResponse objects. The array of users is sorted in place and then returned.
 * By default, assumes the resulting users will be displayed in the "Last, First" format.
 */
export const sortFormattedUsers = (users: UserResponse[], format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): UserResponse[] => {
    return users.sort((userA, userB) => {
        return compareUsersBySubjectForSorting(userA.cognito_subject, userB.cognito_subject, users, format);
    });
};

// TODO: Centralize other sets of roles here, and possibly move them to a more appropriate file.

/**
 * The roles a user must have in order to access Analytics in the Reporting and Exports module.
 */
export const ANALYTICS_ROLES = [Role.REPORTING_USER];

/**
 * The roles a user must have in order to:
 * * access the Control Automation module
 * * see the Control Automation tab in Dashboards (reporting users can see it as well)
 * * see the Control Automation section in Exports (reporting users can see it as well)
 * * see Alerts in the Header
 */
export const CONTROL_AUTOMATION_ROLES = [Role.CONTROL_USER, Role.CONTROL_USER_READ_ONLY];

/**
 * The subset of `RISK_ROLES` that manages the risk process itself, rather than individual risks.
 * These are the roles a user must have in order to access Risk Review (both the module and the export).
 */
export const RISK_MANAGER_ROLES = [Role.RISK_MANAGER, Role.RISK_MANAGER_READ_ONLY];

/**
 * The roles a user must have in order to:
 * * access the Risk Management module
 * * see the Risk Management tab in Dashboards (reporting users can see it as well)
 * * see the Risk Management section in Exports (reporting users can see it as well)
 * Access to Risk Review is further restricted to `RISK_MANAGER_ROLES`.
 */
export const RISK_ROLES = [...RISK_MANAGER_ROLES, Role.RISK_USER, Role.RISK_USER_READ_ONLY];

/**
 * The roles a user must have in order to:
 * * have full access to the Third-Party Risk Management module
 * * see the Third-Party Risk Management tab in Dashboards (reporting users can see it as well)
 * * see the Third-Party Risk Management section in Exports (reporting users can see it as well)
 * Any users can manage their own drafts of new third parties and third party services. All other TPRM operations are restricted to users with these roles. THIRD_PARTY_USER_READ_ONLY users, of course, cannot edit anything, but can view everything.
 */
export const TPRM_ROLES = [Role.THIRD_PARTY_USER, Role.THIRD_PARTY_USER_READ_ONLY];

/**
 * The roles a user must have in order to:
 * * have full access to the UCRM module
 * * see the UCRM section in Exports (reporting users can see it as well)
 * Any users can manage their own drafts of new businesses and use cases. All other UCRM operations are restricted to users with these roles. UCRM_USER_READ_ONLY users, of course, cannot edit anything, but can view everything.
 */
export const UCRM_ROLES = [Role.UCRM_USER, Role.UCRM_USER_READ_ONLY];

/**
 * The roles a user must have in order to access the Reporting and Exports module.
 * At the time of writing, this is every role. Access to Analytics is further restricted to `ANALYTICS_ROLES`.
 */
export const REPORTING_AND_EXPORTS_ROLES = [Role.REPORTING_USER, ...CONTROL_AUTOMATION_ROLES, ...UCRM_ROLES, ...RISK_ROLES, ...TPRM_ROLES];

/** For UI shared by TPRM and UCRM: returns the roles that grant full access to the given module (`TPRM_ROLES` or `UCRM_ROLES`). */
export const getDueDiligenceModuleRoles = (module: DueDiligenceModule): Role[] => (module === ProductModule.UCRM ? UCRM_ROLES : TPRM_ROLES);

/** For UI shared by TPRM and UCRM: returns the (non-read-only) role that a user must have in order to be assigned work in the given module, such as owning an assessment. */
export const getDueDiligenceModuleReadWriteRole = (module: DueDiligenceModule): Role => (module === ProductModule.UCRM ? Role.UCRM_USER : Role.THIRD_PARTY_USER);

/** Returns true if the user has a TPRM-specific role. This differentiates between TPRM roles and non-TPRM roles; it does NOT differentiate between users who have read-only TPRM access vs. read/write-TPRM access. */
export const userHasFullAccessToTprm = (user: UserResponse): boolean => {
    return userHasAuthorizedRole(user.roles, TPRM_ROLES);
};

// TODO: This could be improved if the logged-in user were available in the app-wide context, and could be accessed here via context, rather than passing in roles.
export const loggedInUserHasFullAccessToTprm = (loggedInUserRoles: Role[]): boolean => {
    return userHasAuthorizedRole(loggedInUserRoles, TPRM_ROLES);
};

/** Returns true if the user has a UCRM-specific role. Like `userHasFullAccessToTprm`, this does NOT differentiate between read-only and read/write access. */
export const userHasFullAccessToUcrm = (user: UserResponse): boolean => {
    return userHasAuthorizedRole(user.roles, UCRM_ROLES);
};

export const loggedInUserHasFullAccessToUcrm = (loggedInUserRoles: Role[]): boolean => {
    return userHasAuthorizedRole(loggedInUserRoles, UCRM_ROLES);
};
